import React, {
  Dispatch, Ref, SetStateAction, useEffect, useRef, useState,
} from 'react';
import {
  View, StyleSheet, Text, FlatList, Pressable,
} from 'react-native';
import { getAllCountries } from 'country-state-city/src/country';
import { FontAwesome } from '@expo/vector-icons';
import Colors from '../../../../../constants/Colors';
import Style from '../../../../../constants/Style';
import { COUNTRY_EMPTY_ERROR } from './errorMessages';
import titleCase from '../../../../../components/TitleCase';
import isMobile from '../../../../../components/IsMobile';

const characterResetCountdownInterval: number = 500;

export type Country = {
  isoCode: string,
  name: string,
  flag: string,
}

interface CountrySelectorProps {
  selectedCountry: Country,
  setSelectedCountry: Dispatch<SetStateAction<Country>>,
  countryInvalid: boolean,
  countryDropdownShown: boolean,
  setCountryDropdownShown: Dispatch<SetStateAction<boolean>>,
}

const CountrySelector = ({
  selectedCountry, setSelectedCountry, countryInvalid, countryDropdownShown,
  setCountryDropdownShown,
}: CountrySelectorProps) => {
  const countryList: Ref<FlatList> = useRef<FlatList>(null);
  const [charactersPressed, setCharactersPressed] = useState<string>('');
  const [latestCharacter, setLatestCharacter] = useState<string>('');
  let characterResetTimeout: number = 0;
  let countdown = null;
  const [showFlag, setShowFlag] = useState<boolean>(false);

  useEffect(() => {
    if (isMobile) return null;
    // @ts-ignore
    // eslint-disable-next-line no-undef
    setShowFlag(window?.navigator?.userAgentData?.platform?.toLowerCase() === 'macos');
    // eslint-disable-next-line no-undef
    window.addEventListener('keydown', saveCharacter, true);
    // eslint-disable-next-line no-undef
    return window.removeEventListener('keydown', saveCharacter);
  }, []);

  const saveCharacter = (event) => {
    if (!(event.key.length === 1 && /^[a-zA-Z ]*$/.test(event.key))) return;
    setLatestCharacter(event.key.toLowerCase());
  };

  useEffect(() => {
    setCharactersPressed(charactersPressed + latestCharacter);
    clearInterval(countdown);
    characterResetTimeout = 2000;
    countdown = setInterval(() => {
      if (characterResetTimeout >= characterResetCountdownInterval) {
        characterResetTimeout -= characterResetCountdownInterval;
      } else {
        clearTimeout(countdown);
        setCharactersPressed('');
      }
    }, characterResetCountdownInterval);
  }, [latestCharacter]);

  useEffect(() => {
    if (!(charactersPressed?.length > 0)) return;
    let found: boolean = false;
    getAllCountries().forEach((country: Country, index: number) => {
      if (!found && country.name.toLowerCase().indexOf(charactersPressed.toLowerCase()) === 0) {
        countryList.current?.scrollToIndex({ index, animated: false });
        found = true;
      }
    });
    if (found) return;
    getAllCountries().forEach((country: Country, index: number) => {
      if (!found && country.name.toLowerCase().includes(charactersPressed.toLowerCase())) {
        countryList.current?.scrollToIndex({ index, animated: false });
        found = true;
      }
    });
  }, [charactersPressed]);

  const scrollToSelectedCountry: () => void = () => {
    countryList?.current?.scrollToIndex({
      index: getAllCountries()
        .findIndex((country: Country) => country.isoCode === (selectedCountry?.isoCode ?? 'IE')),
      animated: false,
    });
  };

  return <View style={styles.container}>
    <Pressable
      style={[Style.row, styles.option, styles.dropdownButton, countryInvalid && styles.invalid]}
      onPress={() => setCountryDropdownShown(!countryDropdownShown)}
    >
      <View style={Style.row}>
        <Text style={styles.text}>{selectedCountry?.name ?? 'Choose a country'}</Text>
        {showFlag && selectedCountry?.flag
          && <Text style={styles.flag}>{selectedCountry.flag}</Text>}
      </View>
      <FontAwesome
        size={20}
        color={countryInvalid ? Colors.light.hereworksRed500 : Colors.light.hereworksBlue100}
        name={countryDropdownShown ? 'angle-up' : 'angle-down'}
      />
    </Pressable>
    {countryDropdownShown ? <FlatList
      testID='countries-list'
      initialNumToRender={300}
      style={styles.dropdownView}
      ref={countryList}
      onScrollToIndexFailed={() => { }}
      onLayout={scrollToSelectedCountry}
      data={getAllCountries()}
      renderItem={({ item }) => {
        const selected: boolean = selectedCountry?.isoCode === item.isoCode;
        return <Pressable
          style={(state: { hovered: boolean, pressed: boolean }) => ([
            Style.row, state.hovered && Style.listItemHovered, selected && styles.optionSelected,
          ])}
          onPress={() => {
            setSelectedCountry(item);
            setCountryDropdownShown(false);
          }}
        >
          <Text style={[
            styles.text, styles.option, selected && styles.optionTextSelected,
          ]}>
            {titleCase(item?.name)}
          </Text>
          {showFlag && item?.flag && <Text style={styles.flag}>{item?.flag}</Text>}
        </Pressable>;
      }}
      keyExtractor={(country: Country) => `country_option_${country.isoCode}`}
    /> : (countryInvalid && <Text style={styles.errorText}>{COUNTRY_EMPTY_ERROR}</Text>)}
  </View>;
};

const styles = StyleSheet.create({
  container: {
    alignSelf: 'stretch',
    marginTop: 4,
    zIndex: 3,
  },
  text: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  dropdownButton: {
    borderRadius: 6,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    height: 44,
  },
  flag: {
    fontSize: 28,
    marginStart: 4,
    lineHeight: 24,
    marginEnd: 18,
  },
  dropdownView: {
    top: 52,
    maxHeight: 180,
    width: 170,
    borderWidth: 1,
    borderRadius: 6,
    backgroundColor: '#FFF',
    position: 'absolute',
    borderColor: Colors.light.hereworksBlue100,
  },
  option: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  optionSelected: {
    backgroundColor: Colors.light.hereworksDarkBlue,
  },
  optionTextSelected: {
    backgroundColor: Colors.light.hereworksDarkBlue,
    color: '#FFF',
  },
  invalid: {
    borderColor: Colors.light.hereworksRed500,
  },
  errorText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksRed500,
  },
});

export default CountrySelector;
